import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { abilityPalette } from '../../abilityPalette';
import { ringBlast, groundCloud, glintScatter, risingColumn } from './burstBuilders';
import { WARLOCK_STATE_VFX } from './WarlockStateVFX';
import { DRAIN_LIFE_IMPACT_VFX } from './DrainLifeImpactVFX';

// =============================================================================
// WARLOCK EFFECTS — FX grammar buildout lane 2 (2026-07-17)
// =============================================================================
//
// Palette: shadow purple base family (0x2A0A3A deep purple-black · 0x551188
// violet · 0x7D3C98 dim orchid) with per-buff accent tones:
//   fel   = sickly green 0x66FF44
//   demon = ember red    0xCC3311
//   pact  = blood crimson 0xB3122E → mana violet 0x8844EE
//
// NAMING NOTE (conductor action needed): the three buff cast beats below are
// authored in content/abilities/warlock.toml as the GENERIC
// `cast_effect = "glow"` (life_tap, fel_armor, demon_armor). Claiming the
// literal name 'glow' would hijack every generic glow in the game, so these
// defs ship under proposed bespoke names and need a per-ability TOML rename
// (cast_effect = "fel_armor_shroud" etc.) before static registration wins.
// =============================================================================

/**
 * Shadow Cloud — the warlock kit's shared impact (6 abilities: corruption,
 * curse_of_agony, siphon_life, curse_of_weakness, curse_of_tongues,
 * curse_of_elements). SPEC: a lingering dark mist — a deep purple-black
 * ground cloud swells slowly under a brighter violet inner pall, slow shadow
 * wisps curl upward, and a dim violet ring breathes out. OMINOUS and
 * lingering — a settling miasma, not a pop.
 */
const SHADOW_CLOUD: VFXEffectConfig = {
  id: 'shadow_cloud',
  name: 'Shadow Cloud',
  category: 'burst',
  description: 'Lingering dark mist — deep purple-black ground cloud + slow shadow wisps + dim violet ring',
  primitives: [
    // Outer pall — deep purple-black, wide and slow (the LINGER).
    groundCloud({
      width: 96, height: 34, color: 0x2A0A3A, alpha: 0.55, depth: 92,
      swellTo: 1.7, durationMs: 950,
      stroke: { color: 0x551188, width: 2 },
    }),
    // Inner miasma — brighter violet, tighter, slightly quicker.
    groundCloud({
      width: 56, height: 22, color: 0x551188, alpha: 0.5, depth: 93,
      offsetY: 14, swellTo: 1.45, durationMs: 780,
    }),
    // Dim violet ring blast — low alpha, slow expand; a breath, not a bang.
    ringBlast({
      radius: 26, color: 0x7D3C98, alpha: 0.4, depth: 94, glow: true,
      stroke: { color: 0x551188, width: 2 },
      expandTo: 2.0, durationMs: 700, ease: 'Sine.easeOut',
    }),
    // Bespoke flavor: slow shadow wisps curling up out of the cloud —
    // languid one-shot rise (the mist coming alive), NOT a spark burst.
    {
      type: 'circle', radius: 5, color: 0x3D1155, alpha: 0.85, depth: 95, glow: true,
      scatter: { count: 6, radius: 34 },
      tween: { y: -22, alpha: 0, scale: 1.6, duration: 900, ease: 'Sine.easeOut', delay: 120 },
    },
  ],
};

/**
 * Fel Armor Shroud — cast beat for fel_armor (proposed TOML name; authored
 * as generic 'glow' today). SPEC: a shadow-purple shell snaps around the
 * caster with a sickly green corroded edge, and fel droplets sweat DOWN off
 * the shell. Accompanies the generator's persistent status aura.
 */
const FEL_ARMOR_SHROUD: VFXEffectConfig = {
  id: 'fel_armor_shroud',
  name: 'Fel Armor Shroud',
  category: 'burst',
  description: 'Shell-snap cast beat — shadow shell with sickly green edge + fel drips',
  primitives: [
    // Violet ring breathing out as the armor takes.
    ringBlast({
      radius: 24, color: 0x551188, alpha: 0.5, depth: 93,
      expandTo: 1.9, durationMs: 420, ease: 'Power2',
    }),
    // Bespoke flavor: the shell itself — body-sized ellipse locking in, fel
    // green corroded edge over the shadow base, one flash then gone (the
    // persistent aura owns the lingering look).
    {
      type: 'ellipse', width: 62, height: 78, color: 0x2A0A3A, alpha: 0.55,
      depth: 94, glow: true, stroke: { color: 0x66FF44, width: 3 },
      tween: { alpha: 0, scale: 1.12, duration: 480, ease: 'Sine.easeOut' },
    },
    // Bespoke flavor: fel sweat — sickly droplets running DOWN the shell.
    {
      type: 'circle', radius: 2.5, color: 0x66FF44, alpha: 0.95, depth: 95, glow: true,
      scatter: { count: 7, radius: 30 },
      tween: { y: 16, alpha: 0, scale: 0.5, duration: 520, ease: 'Sine.easeIn', delay: 90 },
    },
  ],
};

/**
 * Demon Armor Shroud — cast beat for demon_armor (proposed TOML name;
 * authored as generic 'glow' today). SPEC: an ember-red furnace column
 * flares up behind the caster as the demonic plate takes — a shadow shell
 * with a smoldering red edge, ember glints spitting off it.
 */
const DEMON_ARMOR_SHROUD: VFXEffectConfig = {
  id: 'demon_armor_shroud',
  name: 'Demon Armor Shroud',
  category: 'burst',
  description: 'Furnace cast beat — ember column + shadow shell with smoldering red edge',
  primitives: [
    // Ember furnace column rising behind the caster — the demonic forge beat.
    risingColumn({
      width: 26, height: 74, color: 0xCC3311, alpha: 0.6, depth: 92, glow: true,
      riseBy: 28, durationMs: 460,
    }),
    // Bespoke flavor: the demon plate — shadow shell, smoldering ember edge.
    {
      type: 'ellipse', width: 66, height: 80, color: 0x2A0A3A, alpha: 0.6,
      depth: 94, stroke: { color: 0xCC3311, width: 3 },
      tween: { alpha: 0, scale: 1.1, duration: 500, ease: 'Power2' },
    },
    // Ember glints spitting off the fresh plate.
    glintScatter({ count: 6, spread: 34, color: 0xFF6633, radius: 2.5, depth: 96, durationMs: 300, delayMs: 110 }),
  ],
};

/**
 * Bloodwell's body-centered cast beat. The contracting crimson core and
 * rising violet motes identify the pact; accepted receipts alone own its
 * resource amounts. Color roles come from the canonical ability palette.
 */
const bloodwellPalette = abilityPalette('life_tap')?.palette ?? [];
const LIFE_TAP_PACT: VFXEffectConfig = {
  id: 'life_tap_pact',
  name: 'Bloodwell Pact',
  category: 'burst',
  description: 'A crimson core contracts within a violet ring as pale-edged motes rise around the caster',
  primitives: [
    // Dim shadow ring grounding the exchange.
    ringBlast({
      radius: 20, color: bloodwellPalette[1], alpha: 0.35, depth: 93,
      stroke: { color: bloodwellPalette[2], width: 2 },
      expandTo: 1.7, durationMs: 480, ease: 'Sine.easeOut',
    }),
    // Bespoke flavor: the blood price — crimson core contracting INWARD
    // (scale down = taken), the inverse read of every outward burst.
    {
      type: 'circle', radius: 22, color: bloodwellPalette[0], alpha: 0.75, depth: 94, glow: true,
      tween: { alpha: 0, scale: 0.25, duration: 420, ease: 'Power2' },
    },
    // Bespoke flavor: mana returned — violet motes rising from the exchange.
    {
      type: 'circle', radius: 3, color: bloodwellPalette[1], alpha: 0.95, depth: 96, glow: true,
      scatter: { count: 6, radius: 22 },
      tween: { y: -26, alpha: 0, scale: 1.4, duration: 560, ease: 'Sine.easeOut', delay: 140 },
    },
  ],
};

// =============================================================================
// DRAIN LINKS — beam-builders Phase 1 (drain connecting-visuals slice)
// =============================================================================
//
// Before this, drain_life / drain_soul had ZERO connecting visual — each tick
// coughed a preset_shadow_cloud next to the caster and the channel vfxId fell
// through to a school burst (unregistered name). These defs claim the channel
// vfxIds with a two-endpoint `link` beam (BeamLinkSystem stretches + rotates
// caster→target every frame) plus flow motes lerping back along the link —
// the "life flowing back" siphon read. ChannelBehavior spawns them ONCE per
// channel (duration-killed; combat:cast_cancelled tears them down early).
//
// NO tweens on linked primitives by contract (the link owns position — and a
// channel's duration override would stretch any authored tween to the full
// channel length anyway). Multi-tone: link core + mote accent are distinct
// fills, so the palette lock protects the authored look.
// Name check (lane rule 5): grep of src/game/effects at build time shows no
// prior registered 'drain_life'/'drain_soul' defs — namedEffectGenerator's
// school-burst realization loses to static registration (it skips registered
// ids).

/**
 * Drain Life link — violet siphon beam caster→target, sickly-green life
 * motes flowing BACK to the caster. Wobble (segments+jitter) keeps it
 * organic rather than a laser.
 */
const DRAIN_LIFE_LINK: VFXEffectConfig = {
  id: 'drain_life',
  name: 'Drain Life Link',
  category: 'burst',
  description: 'Channel siphon — violet caster→target link + green life motes flowing to the caster',
  primitives: [
    {
      type: 'beam', beamLength: 300, beamWidth: 4, beamSegments: 5,
      lineJitter: 10, color: 0x9b59b6, alpha: 0.85, depth: 93, glow: true,
      link: { to: 'target', maxLengthPx: 520 },
    },
    // Life packets — staggered thirds of the cycle, flowing to the caster.
    {
      type: 'circle', radius: 4, color: 0x2ecc71, alpha: 0.95, depth: 94, glow: true,
      link: { to: 'target', mote: { periodMs: 900, phase: 0, direction: 'toCaster' } },
    },
    {
      type: 'circle', radius: 3, color: 0x2ecc71, alpha: 0.8, depth: 94, glow: true,
      link: { to: 'target', mote: { periodMs: 900, phase: 0.33, direction: 'toCaster' } },
    },
    {
      type: 'circle', radius: 3.5, color: 0x66ff88, alpha: 0.9, depth: 94, glow: true,
      link: { to: 'target', mote: { periodMs: 900, phase: 0.66, direction: 'toCaster' } },
    },
  ],
};

/**
 * Drain Soul link — darker, slower sibling: deep-teal soul tether with pale
 * wisp motes drifting back. Reads heavier than drain_life (execute channel).
 */
const DRAIN_SOUL_LINK: VFXEffectConfig = {
  id: 'drain_soul',
  name: 'Drain Soul Link',
  category: 'burst',
  description: 'Channel soul-tether — deep teal caster→target link + pale wisp motes flowing to the caster',
  primitives: [
    {
      type: 'beam', beamLength: 300, beamWidth: 5, beamSegments: 6,
      lineJitter: 14, color: 0x1b4345, alpha: 0.9, depth: 93, glow: true,
      link: { to: 'target', maxLengthPx: 520 },
    },
    // Soul wisps — slower cycle than drain_life (heavier drag).
    {
      type: 'circle', radius: 4.5, color: 0x63e0c4, alpha: 0.9, depth: 94, glow: true,
      link: { to: 'target', mote: { periodMs: 1300, phase: 0, direction: 'toCaster' } },
    },
    {
      type: 'circle', radius: 3.5, color: 0x63e0c4, alpha: 0.75, depth: 94, glow: true,
      link: { to: 'target', mote: { periodMs: 1300, phase: 0.5, direction: 'toCaster' } },
    },
  ],
};

// =============================================================================
// EXPORTS
// =============================================================================

export const WARLOCK_VFX: VFXEffectConfig[] = [
  SHADOW_CLOUD,
  FEL_ARMOR_SHROUD,
  DEMON_ARMOR_SHROUD,
  LIFE_TAP_PACT,
  DRAIN_LIFE_LINK,
  DRAIN_SOUL_LINK,
  ...WARLOCK_STATE_VFX,
  ...DRAIN_LIFE_IMPACT_VFX,
];
